import { Component, HostBinding, Input, OnChanges } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock, chartColors, countedFigure } from '../report-aggregation';
import { textOn } from './summary-boxes.component';

interface Stage {
  label: string;
  /** The stage's own share, as it is printed. */
  percent: number;
  count: number;
  /** Share of the card, relative to the widest stage. */
  width: number;
  background: string;
  color: string;
  /** How much of the previous stage was lost getting here, or null for the first. */
  dropOff: string | null;
  /** Too narrow to hold its own figure, so the figure is printed under it instead. */
  narrow: boolean;
}

/**
 * Below this share of the card a bar cannot hold a name and a figure at any useful length, so both
 * go underneath rather than being drawn where they cannot be read.
 */
const MIN_INSIDE_WIDTH = 24;
/** A stage nobody reached still gets a sliver, so the row reads as a stage rather than as a gap. */
const MIN_WIDTH = 2;

@Component({
  selector: 'app-funnel-block',
  standalone: false,
  template: `
    @if (!stages.length) {
      <p class="grey4 nav-font12 mb-0">
        {{ nothingCounted ? 'Tick at least one answer to count.' : 'No data for this funnel.' }}
      </p>
    }

    <ol class="funnel list-unstyled mb-0">
      @for (stage of stages; track stage.label) {
        <li class="stage">
          <div class="stage-bar"
               [style.width.%]="stage.width"
               [style.background-color]="stage.background"
               [style.color]="stage.color"
               [attr.title]="stage.label + ' — ' + stage.count + ' respondents'">
            @if (!stage.narrow) {
              @if (!hideLabels) {
                <span class="stage-name nav-font14">{{ stage.label }}</span>
              }
              <span class="stage-value nav-font16 font-weight-bold">
                {{ stage.percent | number: '1.0-0' }}%
              </span>
            }
          </div>

          <!-- Too narrow to hold anything: the name and the figure go under the bar rather than
               being drawn across an edge where neither can be read. -->
          @if (stage.narrow) {
            <p class="stage-outside nav-font14 mb-0">
              @if (!hideLabels) {
                <span class="mr-2">{{ stage.label }}</span>
              }
              <span class="font-weight-bold">{{ stage.percent | number: '1.0-0' }}%</span>
            </p>
          }

          @if (stage.dropOff) {
            <p class="stage-drop grey4 nav-font12 mb-0">{{ stage.dropOff }}</p>
          }
        </li>
      }
    </ol>
  `,
  styles: [`
    :host { display: block; }

    .funnel { padding: 0.25rem 0; }

    .stage + .stage { margin-top: 0.5rem; }

    /* Centred rather than left-aligned: a funnel is read as a shape narrowing towards a point, and
       anchoring one edge turns the same numbers into a bar chart. */
    .stage-bar {
      margin: 0 auto;
      min-width: 4px;
      height: 44px;
      border-radius: 3px;
      padding: 0 14px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      overflow: hidden;
    }

    /* Ellipsised rather than wrapped: the bar's height is the funnel's shape, so a name that grew
       to two lines would change the picture. The full text stays in the hover title. */
    .stage-name {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .stage-value {
      flex: 0 0 auto;
      margin-left: auto;
    }

    .stage-outside,
    .stage-drop { text-align: center; }

    .stage-outside {
      color: var(--ecr-ink, #1b2b33);
      margin-top: 0.25rem;
    }

    :host(.is-compact) .stage-bar { height: 34px; }

    .stage-drop { margin-top: 0.25rem; }
  `],
})
export class FunnelBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;
  @Input() editing?: boolean;

  @HostBinding('class.pl-5')
  @HostBinding('class.pr-5')
  get fullWidth(): boolean {
    return this.block?.segmentLocation === 'full';
  }

  /** `compact` is an input, so the styles need it on the host to be able to see it. */
  @HostBinding('class.is-compact')
  get isCompact(): boolean {
    return !!this.compact;
  }

  stages: Stage[] = [];
  hideLabels = false;
  /** Every answer unticked, on a funnel of one question per stage: no stage has a figure. */
  nothingCounted = false;

  ngOnChanges() {
    if (this.block.reportType !== 'funnel') return;

    const extra = this.block.reportExtraData;
    this.hideLabels = !!extra.hideLabels;
    const showDropOff = extra.showDropOff !== false;

    this.nothingCounted = false;
    const figures = this.figures();
    // Relative to the largest stage rather than to 100: a journey that starts at 40% would
    // otherwise be drawn as a funnel with most of the card left blank, and the shape -- which is
    // the whole reason to use this over a bar chart -- would be lost. The printed figure carries
    // the absolute.
    const widest = Math.max(0, ...figures.map(figure => figure.percent));
    const shades = chartColors(this.palette, figures.length);

    this.stages = figures.map((figure, index) => {
      const width = widest > 0 ? Math.max(MIN_WIDTH, (figure.percent / widest) * 100) : MIN_WIDTH;
      const previous = figures[index - 1];
      return {
        ...figure,
        width,
        background: shades[index],
        color: textOn(shades[index]),
        dropOff: showDropOff && previous ? this.dropOff(previous.percent, figure.percent) : null,
        narrow: width < MIN_INSIDE_WIDTH,
      };
    });
  }

  /**
   * One figure per stage. Two shapes arrive here and both mean the same thing:
   * - one series -- the options of a multi-punch question, or a set of columns collapsed into one
   *   by `columnSeries` -- where each *point* is a stage;
   * - several series, one question per stage, where the stage is that question's leading answer,
   *   i.e. the affirmative of a yes/no.
   */
  private figures(): { label: string; percent: number; count: number }[] {
    const known = this.series.filter(entry => entry.known);
    if (!known.length) return [];

    if (known.length === 1) {
      return known[0].points.map(point => ({
        label: point.label,
        percent: point.percent,
        count: point.value,
      }));
    }

    // Every stage question is read for the same answer -- the stages of a real funnel are asked the
    // same way -- so one setting covers them all.
    const answers = this.block.reportType === 'funnel'
      ? this.block.reportExtraData.countedAnswers
      : undefined;
    this.nothingCounted = !!answers && !answers.length;
    if (this.nothingCounted) return [];

    return known.map(entry => {
      const figure = countedFigure(entry.points, answers);
      return { label: entry.name, percent: figure.percent, count: figure.value };
    });
  }

  /**
   * The fall from the previous stage, as a share *of that stage* -- which is the number a funnel
   * exists to make. "60% to 30%" is a 50% drop-off, not a 30-point one, and reporting the points
   * instead makes a halving at the bottom of the funnel look smaller than one at the top.
   */
  private dropOff(from: number, to: number): string | null {
    if (from <= 0) return null;
    const lost = Math.round(((from - to) / from) * 100);
    if (lost <= 0) return null;
    return `${lost}% drop-off`;
  }
}
